/*
 * TFRS Home Page — Capabilities-Style Formatting
 * Uses Capabilities page design language: dark bg, gold accents, section labels,
 * bordered stat pills, left-bordered capability cards, numbered technical items.
 * Real Drive photos: Klein ISD K9 Tahoe, Kemah builds, wiring/install details.
 */

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { ResponsiveStorageImage } from "@/components/ResponsiveStorageImage";
import { useSeo } from "@/hooks/useSeo";
import { getSeoMetadata } from "@shared/seoMetadata";
import {
  TIPS_CONTRACT_NUMBER,
  TIPS_ORGANIZATION,
  TIPS_SHORT_NAME,
  SBA_CERTIFICATION_COUNT,
} from "@shared/companyFacts";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Zap,
  Shield,
  AlertTriangle,
  Network,
  FileCheck,
  Award,
  Pause,
  Play,
  ImageOff,
} from "lucide-react";

// ─── Image constants ─────────────────────────────────────────────────────────

type ResponsiveImageVariant = { width: number; src: string };
type ResponsiveImage = {
  src: string;
  originalSrc: string;
  srcSet?: string;
  sizes?: string;
  width: number;
  height: number;
};

const OPTIMIZED_MEDIA_BASE = "/optimized-media";
const RESPONSIVE_IMAGE_WIDTHS = [480, 640, 768, 1024, 1600] as const;

function optimizedMediaPath(src: string, width: number) {
  const filename = src.split("/").pop() ?? src;
  const baseName = filename.replace(/\.[^.]+$/, "");
  return `${OPTIMIZED_MEDIA_BASE}/${baseName}-${width}.webp`;
}

function responsiveImage(src: string, width: number, height: number, sizes: string): ResponsiveImage {
  const variants: ResponsiveImageVariant[] = RESPONSIVE_IMAGE_WIDTHS.map((variantWidth) => ({
    width: variantWidth,
    src: optimizedMediaPath(src, variantWidth),
  }));

  return {
    src: optimizedMediaPath(src, 768),
    originalSrc: src,
    srcSet: variants.map((variant) => `${variant.src} ${variant.width}w`).join(", "),
    sizes,
    width,
    height,
  };
}

// Hero slider — finished vehicles
const HERO_SLIDES = [
  {
    image: responsiveImage("/manus-storage/IMG_1932_d2bcd1b1.jpeg", 1600, 1000, "(max-width: 767px) 100vw, 100vw"),
    caption: "Klein ISD Police Department · K9 Tahoe",
    label: "COMPLETED BUILD",
  },
  {
    image: responsiveImage("/manus-storage/klein-tahoe-front-1_4dc98ac8.jpg", 1600, 1000, "(max-width: 767px) 100vw, 100vw"),
    caption: "Klein ISD Police Department · Tahoe Front Exterior",
    label: "COMPLETED BUILD",
  },
  {
    image: responsiveImage("/manus-storage/kemah-k9-night-exterior_39b3c25b.jpg", 1600, 1000, "(max-width: 767px) 100vw, 100vw"),
    caption: "Kemah Police Department · K9 Transport · Night Delivery",
    label: "COMPLETED BUILD",
  },
  {
    image: responsiveImage("/manus-storage/work-truck-service-body_78a414ca.jpg", 1600, 1000, "(max-width: 767px) 100vw, 100vw"),
    caption: "Fast Attack Brush Truck · Fire Apparatus Build",
    label: "COMPLETED BUILD",
  },
];

// Gallery — interior / build detail
const GALLERY_IMAGES = [
  { ...responsiveImage("/manus-storage/klein-tahoe-front-1_4dc98ac8.jpg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "Klein ISD Police Department · Tahoe Exterior" },
  { ...responsiveImage("/manus-storage/kemah-k9-night-exterior_39b3c25b.jpg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "Kemah PD · K9 Transport · Night Delivery" },
  { ...responsiveImage("/manus-storage/kemah-interior-partition_73a9bb51.jpg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "Interior Partition + Cage · Kemah Build" },
  { ...responsiveImage("/manus-storage/high-water-rescue-truck_70fb8dc6.jpg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "Manatee County High Water Rescue · Water Rescue 1 · Final Inspection" },
  { ...responsiveImage("/manus-storage/work-truck-service-body_78a414ca.jpg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "Fast Attack Brush Truck · Fire Apparatus Build" },
  { ...responsiveImage("/manus-storage/IMG_2317_24c3056e.jpeg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "Rear Transport Interior · Red Seatbelt System", fit: "contain" as const },
];

// Technical craftsmanship
// "fit: contain" flags source photos shot in portrait/close-up orientation
// that a fixed-aspect-ratio object-cover crop would otherwise slice into an
// unrecognizable sliver (see docs/engineering/backlog -- reported as
// "badly cropped images").
const CRAFT_IMAGES = [
  { ...responsiveImage("/manus-storage/kemah-relay-block_1cc9f52f.jpg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "Labeled Relay Block · Cruise / Flash / Area Zones", fit: "contain" as const },
  { ...responsiveImage("/manus-storage/kemah-wiring-harness_a889269a.jpg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "Custom Wiring Harness · 20+ Labeled Circuits", fit: "contain" as const },
  { ...responsiveImage("/manus-storage/kemah-interior-partition_73a9bb51.jpg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "Interior Partition + Cage Installation" },
  { ...responsiveImage("/manus-storage/high-water-rescue-2_b21f2a36.jpg", 1200, 900, "(min-width: 768px) 33vw, 50vw"), caption: "High Water Rescue · Final Inspection" },
];

// ─── Brand partner logos ─────────────────────────────────────────────────────

const PARTNER_LOGO_WIDTH = 160;
const PARTNER_LOGO_HEIGHT = 80;
const SBA_BADGE_WIDTH = 64;
const SBA_BADGE_HEIGHT = 80;

const PARTNER_LOGOS = [
  { src: "/manus-storage/iWLJVZtGJhZC_aa592c21.jpg", name: "WHELEN", desc: "WHELEN partner equipment and vehicle upfit components." },
  { src: "/manus-storage/Federal-Signal-150x150_7a6afae0.png", name: "FED SIG", desc: "FED SIG partner equipment and vehicle upfit components." },
  { src: "/manus-storage/Havis-150x150_818d0030.png", name: "HAVIS", desc: "HAVIS partner equipment and vehicle upfit components." },
  { src: "/manus-storage/Pro-gard_b5819e82.png", name: "PRO GUARD", desc: "PRO GUARD partner equipment and vehicle upfit components." },
  { src: "/manus-storage/aCXVkaAaiEP3_92d54ad9.png", name: "A.R.E", desc: "A.R.E partner equipment and vehicle upfit components." },
  { src: "/manus-storage/RVWnlPM9VRe6_2adaf10c.jpg", name: "WESTIN", desc: "WESTIN partner equipment and vehicle upfit components." },
  { src: "/manus-storage/Setina_52807315.png", name: "SETINA", desc: "SETINA partner equipment and vehicle upfit components." },
  { src: "/manus-storage/Plastix-Plus-150x150_ac6fd04b.png", name: "PLASTICS PLUS", desc: "PLASTICS PLUS partner equipment and vehicle upfit components." },
  { src: "/manus-storage/7CHKwjVbDFML_0bfe895b.png", name: "ESTES", desc: "ESTES partner equipment and vehicle upfit components." },
  { src: "/manus-storage/tN48PAicEuek_7fe8e044.png", name: "HIGHVIZ LED", desc: "HIGHVIZ LED partner equipment and vehicle upfit components." },
  { src: "/manus-storage/IrKp23q8vnLY_1b6ca7a6.png", name: "ANGEL ARMOR", desc: "ANGEL ARMOR partner equipment and vehicle upfit components." },
  { src: "/manus-storage/mtXHkHZ9MMMA_1b6cd55d.webp", name: "Q ARMOR", desc: "Q ARMOR partner equipment and vehicle upfit components." },
  { src: "/manus-storage/2PlLK0DyRw9H_9d0d8d6b.png", name: "FORD", desc: "FORD partner equipment and vehicle upfit components." },
  { src: "/manus-storage/B8AxSU1MUFlx_1eb42b7c.png", name: "DODGE", desc: "DODGE partner equipment and vehicle upfit components." },
  { src: "/manus-storage/z0ouEbA7FSZy_e9ebe769.jpg", name: "GM", desc: "GM partner equipment and vehicle upfit components." },
  { src: "/manus-storage/911-Circuits-white_4cf0114a.png", name: "911 CIRCUITS", desc: "911 CIRCUITS partner equipment and vehicle upfit components." },
  { src: "/manus-storage/American-Aluminum-1-150x150_79eefc49.png", name: "AMERICAN ALUMINUM", desc: "AMERICAN ALUMINUM partner equipment and vehicle upfit components." },
  { src: "/manus-storage/Ace-K9-white-150x150_569b6f82.png", name: "ACE K9", desc: "ACE K9 partner equipment and vehicle upfit components." },
  { src: "/manus-storage/dlPXZmWWoPS7_7d124993.jpg", name: "TRUCK VAULT", desc: "TRUCK VAULT partner equipment and vehicle upfit components." },
  { src: "/manus-storage/tk0h1GI2hb8r_c2fa70a0.png", name: "TUFFY PRODUCTS", desc: "TUFFY PRODUCTS partner equipment and vehicle upfit components." },
  { src: "/manus-storage/yeC1LFkryT6l_79a775c2.png", name: "GETAC", desc: "GETAC partner equipment and vehicle upfit components." },
  { src: "/manus-storage/Y7hZfSFJqHEe_f82d79e4.png", name: "DEERSKIN MFG", desc: "DEERSKIN MFG partner equipment and vehicle upfit components." },
  { src: "/manus-storage/zUzQxC2dTfpA_f148fed2.png", name: "THUNDERSTRUCK MFG", desc: "THUNDERSTRUCK MFG partner equipment and vehicle upfit components." },
  { src: "/manus-storage/dw73hkqQbOdz_031a2320.png", name: "HAMMERHEAD MFG", desc: "HAMMERHEAD MFG partner equipment and vehicle upfit components." }
];

// SBA badges
const SBA_BADGES = [
  { src: "/manus-storage/WOSBCertified_ddaff520.png", alt: "WOSB Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/EDWOSBCertified_0a68642e.png", alt: "EDWOSB Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/SDVOSBCertified_bc6b80e8.png", alt: "SDVOSB Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/Veteran-OwnedCertified_2cdc5b0e.png", alt: "Veteran-Owned Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/HUBZoneCertified_88debbc5.png", alt: "HUBZone Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/8aCertified_a4a3e9cf.png", alt: "8(a) Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
];

// ─── Data ────────────────────────────────────────────────────────────────────

const PERF_STATS = [
  { value: "50+", label: "Agencies Served" },
  { value: "500+", label: "Vehicles Upfitted" },
  { value: "7", label: "Federal Agencies" },
  { value: "10 DAY", label: "Build Cycle", sub: "Once parts on-hand" },
  { value: "∞", label: "Labor Warranty" },
  { value: String(SBA_CERTIFICATION_COUNT), label: "SBA Certifications" },
];

const CAPABILITIES = [
  {
    category: "Law Enforcement",
    color: "border-[#22c55e] text-[#22c55e]",
    title: "Patrol & Tactical Vehicles",
    description:
      "Full-turnkey patrol upfits with ballistic protection, prisoner transport, and the warning/comms hardware your agency already runs.",
    tags: ["Federal Signal", "Whelen", "Angel Armor IIIA", "ProGuard", "Havis", "Westin"],
    href: "/law-enforcement",
  },
  {
    category: "Fire & Wildland",
    color: "border-[#f97316] text-[#f97316]",
    title: "Wildland Brush Trucks",
    description:
      "A 4x4 diesel brush truck built for pump-and-roll wildland operations, with a 500-gallon tank ready to work on delivery.",
    tags: ["500-Gal Tank", "Rowe Pump", "Akron Turret", "6.7L Cummins", "Pump-and-Roll"],
    href: "/fire-brush-trucks",
  },
  {
    category: "Command Vehicles",
    color: "border-[#eab308] text-[#eab308]",
    title: "Fire Command Platforms",
    description:
      "Incident command platforms with scene lighting, lightbars, and console integration — spec'd and procured end to end.",
    tags: ["Federal Signal", "Whelen", "Havis Console", "6,500 Lm Scene", "Full Procurement"],
    href: "/fire-brush-trucks",
  },
  {
    category: "K9 Programs",
    color: "border-[#22c55e] text-[#22c55e]",
    title: "K9 Unit Consultation",
    description:
      "Purpose-built kennel, climate-control, and safety systems for K9 units — on whatever platform your department runs.",
    tags: ["Kennel Systems", "Climate Control", "Vent Alarms", "Havis Console", "Any Platform"],
    href: "/k9-consult",
  },
];

const DIFFERENTIATORS = [
  {
    icon: <Zap size={20} />,
    title: "10-Day Build Cycle",
    description: "Once all parts are on-hand, we complete the full upfit in 10 days — minimizing fleet downtime.",
  },
  {
    icon: <Shield size={20} />,
    title: "Lifetime Labor Warranty",
    description: "Every installation is backed by a lifetime workmanship warranty for the life of the vehicle.",
  },
  {
    icon: <AlertTriangle size={20} />,
    title: "Fed Sig & Whelen Certified",
    description: "Authorized installer for both Federal Signal and Whelen. 5-year Federal Signal lighting warranty standard.",
  },
  {
    icon: <Network size={20} />,
    title: "Premier Partner Network",
    description: "Authorized for Havis, Westin, ProGuard, Plastix Plus, and Angel Armor.",
  },
  {
    icon: <FileCheck size={20} />,
    title: "Cooperative Contracts",
    description: `${TIPS_SHORT_NAME} cooperative purchasing contracts — no lengthy bidding cycles.`,
  },
  {
    icon: <Award size={20} />,
    title: "Veteran-Owned & SBA Certified",
    description: "SDVOSB, VOSB, WOSB, EDWOSB, HUBZone, and SBA 8(a) certified. SAM.gov active.",
  },
];

const AGENCIES = [
  "U.S. Coast Guard",
  "U.S. Marshals Service",
  "Federal Bureau of Investigation",
  "DHS Air & Marine Operations",
  "U.S. Customs & Border Protection",
  "Port Arthur Police Dept.",
  "Dallas ISD Police",
  "TX Parks & Wildlife",
  "Galveston County Sheriff",
  "Texas DPS",
  "Memorial Villages PD",
  "Sealy Police Dept.",
  "Melissa Police Dept.",
  "Harris County SO",
  "Montgomery County SO",
  "Klein ISD Police",
  "Kemah Police Dept.",
  "Texas City Fire Dept.",
  "Dickinson ISD Police",
  "League City Police",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function RevealSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!("IntersectionObserver" in window)) {
      el.classList.add("visible");
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          observer.disconnect();
        }
      },
      { rootMargin: "160px 0px", threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const SbaLightbox = lazy(() => import("@/components/SbaLightbox"));

type GalleryThumbnailImage = Pick<ResponsiveImage, "src" | "originalSrc" | "srcSet" | "sizes" | "width" | "height">;

// Renders a gallery photo with a two-tier fallback: the optimized srcSet,
// then the raw original on optimizer failure (same pattern as the hero
// slider), and finally — if the original also fails to load — a visible
// placeholder instead of bare broken alt text with no box (see GH-212).
function GalleryThumbnail({ image, caption, fit = "cover" }: { image: GalleryThumbnailImage; caption: string; fit?: "cover" | "contain" }) {
  const [loadFailed, setLoadFailed] = useState(false);

  if (loadFailed) {
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center gap-2 border border-white/10 bg-[var(--tfrs-navy-panel)] text-white/40"
        role="img"
        aria-label={caption}
      >
        <ImageOff className="h-8 w-8" aria-hidden="true" />
        <span className="font-eyebrow text-xs tracking-widest uppercase px-3 text-center">{caption}</span>
      </div>
    );
  }

  return (
    <img
      src={image.src}
      srcSet={image.srcSet}
      sizes={image.sizes}
      alt={caption}
      width={image.width}
      height={image.height}
      loading="lazy"
      decoding="async"
      onError={(event) => {
        const el = event.currentTarget;
        if (el.dataset.fallbackApplied) {
          setLoadFailed(true);
          return;
        }
        el.dataset.fallbackApplied = "true";
        el.removeAttribute("srcset");
        el.removeAttribute("sizes");
        el.src = image.originalSrc;
      }}
      className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${fit === "contain" ? "object-contain" : "object-cover"}`}
    />
  );
}

// ─── Hero Slider ─────────────────────────────────────────────────────────────

function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [deferredSlidesReady, setDeferredSlidesReady] = useState(false);
  // WCAG 2.2.2 (Pause, Stop, Hide): auto-rotating content needs a user
  // control to stop it, and it shouldn't start at all for visitors who ask
  // their OS to reduce motion.
  const [userPaused, setUserPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReducedMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const autoRotate = !userPaused && !prefersReducedMotion;
  const total = HERO_SLIDES.length;

  // Slides 1-3 are deferred/lazy-loaded for LCP reasons (see main.tsx's
  // slide-0-only preload), so a slide can become `current` before its image
  // has actually downloaded. Track which slides have real pixels ready in a
  // ref (not state -- nothing here needs to trigger a render on its own) and
  // hold the transition until the target is ready, instead of ever flashing
  // the section's bare navy background between two photos.
  const loadedRef = useRef<Set<number>>(new Set());
  const pendingTargetRef = useRef<number | null>(null);

  const markLoaded = useCallback((i: number) => {
    if (loadedRef.current.has(i)) return;
    loadedRef.current.add(i);
    if (pendingTargetRef.current === i) {
      pendingTargetRef.current = null;
      setCurrent(i);
    }
  }, []);

  const advanceTo = useCallback((target: number) => {
    if (loadedRef.current.has(target)) {
      pendingTargetRef.current = null;
      setCurrent(target);
    } else {
      pendingTargetRef.current = target;
    }
  }, []);

  useEffect(() => {
    if (!autoRotate) return;
    const id = setInterval(() => advanceTo((current + 1) % total), 6000);
    return () => clearInterval(id);
  }, [autoRotate, current, total, advanceTo]);

  useEffect(() => {
    const loadDeferredSlides = () => {
      window.setTimeout(() => setDeferredSlidesReady(true), 1200);
    };

    if (document.readyState === "complete") {
      loadDeferredSlides();
      return;
    }

    window.addEventListener("load", loadDeferredSlides, { once: true });
    return () => window.removeEventListener("load", loadDeferredSlides);
  }, []);

  const prev = () => advanceTo((current - 1 + total) % total);
  const next = () => advanceTo((current + 1) % total);

  return (
    <section className="relative min-h-[100svh] overflow-hidden md:min-h-[92vh]" style={{ background: "var(--tfrs-navy)" }}>
      {/* Slides */}
      {HERO_SLIDES.map((slide, i) => {
        if (i > 0 && !deferredSlidesReady) return null;

        return (
          <picture
            key={slide.image.originalSrc}
            className="absolute inset-0 h-full w-full"
            style={{ opacity: i === current ? 1 : 0 }}
            aria-hidden="true"
          >
            <img
              ref={(el) => {
                if (el?.complete && el.naturalWidth > 0) markLoaded(i);
              }}
              src={slide.image.src}
              srcSet={slide.image.srcSet}
              sizes={slide.image.sizes}
              alt=""
              width={slide.image.width}
              height={slide.image.height}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "low"}
              decoding="async"
              onLoad={() => markLoaded(i)}
              onError={(event) => {
                const img = event.currentTarget;
                if (img.dataset.fallbackApplied) {
                  // Fallback also failed -- stop waiting on this slide so a
                  // broken image can't stall the carousel on the prior slide
                  // forever.
                  markLoaded(i);
                  return;
                }
                img.dataset.fallbackApplied = "true";
                img.removeAttribute("srcset");
                img.removeAttribute("sizes");
                img.src = slide.image.originalSrc;
              }}
              className="h-full w-full object-cover transition-opacity duration-1000"
            />
          </picture>
        );
      })}
      {/* Gradient overlay */}
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to top, rgba(8,13,20,0.98) 0%, rgba(8,13,20,0.82) 45%, rgba(8,13,20,0.58) 100%)" }}
      />

      {/* Content */}
      <div className="absolute inset-0 z-10 flex w-full flex-col justify-center pb-32 pt-24 sm:pb-28 sm:pt-32">
        <div className="max-w-6xl mx-auto flex flex-col items-center px-4 text-center">
          <h1
            className="font-display font-black text-center text-white leading-none mb-4 uppercase"
            style={{ fontSize: "clamp(3rem, 9vw, 7rem)", lineHeight: 0.9, textShadow: "0 2px 20px rgba(0,0,0,0.95), 0 1px 6px rgba(0,0,0,0.8)" }}
          >
            {/* The H1 states what TFRS does and where (matches the page <title>);
                the slogan below keeps its original size and look. The sr-only
                colon makes the heading read as one phrase to screen readers
                and crawlers: "Texas Emergency Vehicle Upfitter: Built for the
                Front Lines". */}
            <span
              className="font-eyebrow mb-4 block font-semibold tracking-[0.22em] text-white/90"
              style={{ fontSize: "clamp(0.75rem, 2.4vw, 1.25rem)", lineHeight: 1.3 }}
            >
              Texas Emergency Vehicle Upfitter
            </span>
            <span className="sr-only">: </span>
            BUILT FOR THE<br />
            <span className="text-[var(--tfrs-red-bright)]">FRONT LINES</span>
          </h1>
          <p className="font-body text-white/90 text-lg max-w-xl mx-auto mb-8 leading-relaxed" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.9)" }}>
            Mission-critical vehicle upfitting with a lifetime workmanship warranty.
            Available through {TIPS_SHORT_NAME} cooperative contracts.
          </p>
          <div className="mx-auto flex w-full max-w-sm flex-col items-stretch justify-center gap-3 mb-10 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center">
            <Link
              href="/configure"
              className="btn-gold font-display text-sm font-bold tracking-widest uppercase px-7 py-3.5 border-2 border-transparent text-[var(--tfrs-navy)] inline-flex w-full items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)] sm:w-auto"
            >
              <span>Build Your Vehicle</span> <ArrowRight size={15} />
            </Link>
            <Link
              href="/quote"
              className="btn-cta w-full justify-center text-sm px-7 py-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)] sm:w-auto"
            >
              <span>Request a Quote</span> <ArrowRight size={15} className="relative z-10" />
            </Link>
          </div>

        </div>
      </div>

      {/* Slide caption + controls */}
      <div className="absolute inset-x-0 bottom-6 z-20 px-4">
        <div className="max-w-6xl mx-auto w-full flex items-center justify-between gap-6 text-left">
          <div className="min-w-0">
            <span className="font-eyebrow text-xs tracking-[0.3em] text-[#c9a84c] uppercase">
              {HERO_SLIDES[current].label}
            </span>
            <p className="font-eyebrow text-xs text-white/85 mt-0.5 truncate sm:whitespace-normal">
              {HERO_SLIDES[current].caption}
            </p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-3">
            {!prefersReducedMotion && (
              <button
                type="button"
                onClick={() => setUserPaused((p) => !p)}
                className="w-9 h-9 border border-white/20 flex items-center justify-center text-white/80 hover:border-white/50 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]"
                aria-label={userPaused ? "Play hero slideshow" : "Pause hero slideshow"}
                aria-pressed={userPaused}
              >
                {userPaused ? <Play size={16} aria-hidden="true" /> : <Pause size={16} aria-hidden="true" />}
              </button>
            )}
            <button
              type="button"
              onClick={prev}
              className="w-9 h-9 border border-white/20 flex items-center justify-center text-white/80 hover:border-white/50 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]"
              aria-label="Show previous hero slide"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="font-eyebrow text-xs text-white/85 tracking-widest">
              {current + 1} / {total}
            </span>
            <button
              type="button"
              onClick={next}
              className="w-9 h-9 border border-white/20 flex items-center justify-center text-white/80 hover:border-white/50 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]"
              aria-label="Show next hero slide"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Home() {
  useSeo(getSeoMetadata("/")!);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const lightboxTriggerRef = useRef<HTMLElement | null>(null);
  const [marqueePaused, setMarqueePaused] = useState(false);

  const openLightbox = useCallback((index: number) => {
    if (index < 0 || index >= SBA_BADGES.length) return;
    lightboxTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setLightboxIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    window.requestAnimationFrame(() => lightboxTriggerRef.current?.focus());
  }, []);

  const prevBadge = useCallback(() => {
    setLightboxIndex((index) => (index === null || SBA_BADGES.length === 0 ? index : (index - 1 + SBA_BADGES.length) % SBA_BADGES.length));
  }, []);

  const nextBadge = useCallback(() => {
    setLightboxIndex((index) => (index === null || SBA_BADGES.length === 0 ? index : (index + 1) % SBA_BADGES.length));
  }, []);

  return (
    <div className="bg-[var(--tfrs-navy)] text-white min-h-screen">

      {/* ── Hero Slider ── */}
      <HeroSlider />

      {/* ── Performance Stats Bar ── */}
      <section className="elevated-strip border-y border-white/10 bg-[var(--tfrs-navy-panel)]">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10">
            {PERF_STATS.map((s) => (
              <div key={s.label} className="bg-[var(--tfrs-navy-panel)] px-5 py-5 text-center">
                <div className="font-display font-black text-3xl text-[#c9a84c] leading-none">
                  {s.value}
                </div>
                {s.sub && (
                  <div className="font-eyebrow text-xs tracking-widest text-[#c9a84c] uppercase mt-0.5">
                    {s.sub}
                  </div>
                )}
                <div className="font-eyebrow text-xs tracking-widest text-white/85 uppercase mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Partner Logo Carousel ── */}
      <section className="py-12 border-b border-[#e5e7eb] bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 mb-8 flex items-center justify-center gap-3">
          <p className="font-eyebrow text-xs tracking-[0.3em] text-[#6b7280] uppercase text-center">
            Trusted Brands &amp; Authorized Partners
          </p>
          <button
            type="button"
            onClick={() => setMarqueePaused((p) => !p)}
            aria-label={marqueePaused ? "Play partner logo scrolling" : "Pause partner logo scrolling"}
            aria-pressed={marqueePaused}
            className="flex-shrink-0 p-1.5 text-[#6b7280] hover:text-[var(--tfrs-navy)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c00a14] focus-visible:ring-offset-2 transition-colors"
          >
            {marqueePaused ? <Play size={12} aria-hidden="true" /> : <Pause size={12} aria-hidden="true" />}
          </button>
        </div>
        {/* Outer mask — fades edges */}
        <div
          className="relative w-full overflow-hidden"
          style={{
            maskImage: "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
          }}
        >
          {/* Scrolling track — two copies for seamless loop */}
          <div
            className="marquee-track-slow flex w-max min-w-full items-center gap-3 sm:gap-5"
            style={marqueePaused ? { animationPlayState: "paused" } : undefined}
          >
            {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map((logo, i) => (
              <div
                key={`${logo.name}-${i}`}
                className="group relative flex h-24 w-44 min-w-[176px] flex-shrink-0 items-center justify-center border border-[#e5e7eb] bg-white px-3 py-2 shadow-sm transition-all duration-300 cursor-default hover:scale-105 hover:border-[#c00a14]/40 hover:shadow-xl sm:h-28 sm:w-52 sm:min-w-[208px] sm:px-5 sm:py-4"
              >
                <div className="flex aspect-[2/1] w-full max-w-[8rem] sm:max-w-[10rem] items-center justify-center">
                  {logo.src ? (
                    <ResponsiveStorageImage
                      source={logo.src}
                      alt={logo.name}
                      width={PARTNER_LOGO_WIDTH}
                      height={PARTNER_LOGO_HEIGHT}
                      sizes="160px"
                      fallbackWidth={160}
                      responsiveWidths={[160, 240, 320]}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full grayscale object-contain transition-all duration-300 group-hover:grayscale-0"
                    />
                  ) : (
                    <span className="font-eyebrow text-center text-sm font-black tracking-widest text-[var(--tfrs-navy)] uppercase">{logo.name}</span>
                  )}
                </div>
                {/* Hover caption card — slides up from bottom */}
                <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out z-30 pointer-events-none">
                  <div className="bg-[var(--tfrs-navy)] border border-[#c9a84c]/30 shadow-2xl px-4 py-3 mx-0">
                    <p className="font-eyebrow text-xs font-bold tracking-widest text-[#c9a84c] uppercase mb-1">{logo.name}</p>
                    <p className="font-body text-xs text-white/80 leading-snug">{logo.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who We Are ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <p className="font-eyebrow text-xs tracking-[0.3em] text-white/85 uppercase mb-3">
              Who We Are
            </p>
            <h2 className="font-display font-black text-3xl md:text-4xl uppercase mb-8 leading-tight">
              What Does Texas First Response Services Do?
            </h2>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-white/80 text-base leading-relaxed mb-4">
                  Texas First Response Services is an SBA 8(a) certified emergency vehicle upfitter based
                  in Dickinson, TX. We build fully configured, mission-ready vehicles for law enforcement,
                  fire/wildland, K9, and commercial fleets — with a 10-day build cycle once parts are
                  on-hand and a lifetime workmanship warranty on every installation we perform.
                </p>
                <p className="text-white/80 text-base leading-relaxed">
                  Builds go on leading platforms — RAM, Ford, Chevrolet — with{" "}
                  <strong className="text-white">Federal Signal and Whelen</strong> warning systems,
                  Angel Armor IIIA ballistic protection, Plastix Plus ballistic glass, Havis consoles,
                  Westin push bumpers, ProGuard prisoner transport, and cooperative contract pricing
                  through {TIPS_SHORT_NAME}.
                </p>
              </div>
              {/* SBA badges */}
              <div>
                <p className="font-eyebrow text-xs tracking-widest text-white/85 uppercase mb-1">
                  SBA Certifications &amp; Federal Qualifications
                </p>
                <p className="font-body text-xs text-[#c9a84c] mb-4 leading-snug">
                  The only SBA 8(a) certified emergency vehicle upfitter in Texas.
                </p>
                {/* Larger grid — badges legible without clicking */}
                <div className="grid grid-cols-3 gap-3">
                  {SBA_BADGES.map((b, i) => (
                    <button
                      type="button"
                      key={b.alt}
                      onClick={() => openLightbox(i)}
                      className="border border-white/10 bg-[var(--tfrs-navy-panel)] p-2 flex items-center justify-center hover:border-[#c9a84c]/50 hover:bg-[#111c2a] transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)] group relative"
                      style={{ minHeight: 90 }}
                      aria-label={`View ${b.alt} certificate`}
                    >
                      <ResponsiveStorageImage
                        source={b.src}
                        alt={b.alt}
                        width={b.width}
                        height={b.height}
                        sizes="64px"
                        fallbackWidth={96}
                        responsiveWidths={[64, 96, 160]}
                        loading="lazy"
                        decoding="async"
                        className="h-20 w-16 object-contain"
                      />
                      {/* Zoom hint on hover */}
                      <span className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity text-white/85 text-xs font-eyebrow tracking-wide">ENLARGE</span>
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {/* SAM.gov Active */}
                  <div className="border border-[#22c55e]/40 bg-[#22c55e]/5 px-3 py-1.5 flex items-center gap-1.5">
                    <span className="font-eyebrow text-xs tracking-widest text-white/85 uppercase">SAM.gov:</span>
                    <span className="font-eyebrow font-bold text-xs text-[#22c55e]">Active</span>
                  </div>
                  {/* CAGE */}
                  <div className="border border-white/10 bg-[var(--tfrs-navy-panel)] px-3 py-1.5 flex items-center gap-1.5">
                    <span className="font-eyebrow text-xs tracking-widest text-white/85 uppercase">CAGE:</span>
                    <span className="font-eyebrow font-bold text-xs text-white">9WTU3</span>
                  </div>
                  {/* UEI — clickable to SAM.gov entry */}
                  <a
                    href="https://search.certifications.sba.gov/profile/W55WVF2EC3J3/9WTU3?page=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-[#c9a84c]/40 bg-[#c9a84c]/5 px-3 py-1.5 flex items-center gap-1.5 hover:bg-[#c9a84c]/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
                    title="View TFRS on SBA Small Business Search"
                  >
                    <span className="font-eyebrow text-xs tracking-widest text-white/85 uppercase">UEI:</span>
                    <span className="font-eyebrow font-bold text-xs text-[#c9a84c] underline decoration-dotted">W55WVF2EC3J3 ↗</span>
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                  {/* NAICS */}
                  <div className="border border-white/10 bg-[var(--tfrs-navy-panel)] px-3 py-1.5 flex items-center gap-1.5">
                    <span className="font-eyebrow text-xs tracking-widest text-white/85 uppercase">NAICS:</span>
                    <span className="font-eyebrow font-bold text-xs text-white">811121</span>
                  </div>
                </div>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ── Core Capabilities ── */}
      <section className="py-20 px-4 bg-[#0a1018]">
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <p className="font-eyebrow text-xs tracking-[0.3em] text-white/85 uppercase mb-3">
              Core Capabilities
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl uppercase mb-4 leading-tight">
              What We Build
            </h2>
            <Link
              href="/capabilities"
              className="inline-block font-eyebrow text-xs font-bold tracking-widest uppercase text-[#c9a84c] hover:text-white mb-12 underline"
            >
              See Full Capabilities &amp; Certifications →
            </Link>
          </RevealSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {CAPABILITIES.map((cap, i) => (
              <RevealSection key={cap.title} delay={i * 80}>
                <Link
                  href={cap.href}
                  className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1018]"
                >
                  <div className={`card-lift border-l-4 ${cap.color.split(" ")[0]} bg-[var(--tfrs-navy-panel)] p-6 h-full hover:bg-[#111c2a] transition-colors cursor-pointer`}>
                    <p className={`font-eyebrow text-xs tracking-widest uppercase mb-2 ${cap.color.split(" ")[1]}`}>
                      {cap.category}
                    </p>
                    <h3 className="font-display font-black text-lg uppercase mb-3 leading-tight">
                      {cap.title}
                    </h3>
                    <p className="text-white/85 text-sm leading-relaxed mb-4">{cap.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {cap.tags.map((tag) => (
                        <span
                          key={tag}
                          className="font-eyebrow text-xs tracking-wider uppercase px-2 py-1 border border-white/15 text-white/85"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Current Offers: Crew Cab Brush Truck + Fleet Vehicle Protection ── */}
      <section className="py-20 px-4" style={{ background: "var(--tfrs-navy-panel)" }}>
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <p className="font-eyebrow text-xs tracking-[0.3em] text-white/85 uppercase mb-3">
              Current Offers
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl uppercase mb-10 leading-tight">
              Ready Now
            </h2>
          </RevealSection>
          <div className="grid md:grid-cols-2 gap-6">
            <RevealSection className="min-w-0">
              <div className="panel-elevated h-full flex flex-col border border-white/10 overflow-hidden" style={{ background: "var(--tfrs-navy)" }}>
                <img
                  src="/manus-storage/f550-platform-comparison_a585985.jpg"
                  alt="Side-by-side comparison rendering of the 2027 Crew Cab Brush Truck on a Ford F-550 platform and a Dodge Ram 5500 platform"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto block"
                />
                <div className="p-8 flex flex-col flex-1">
                  <p className="font-eyebrow text-xs tracking-[0.3em] text-[#c9a84c] uppercase mb-3">
                    For Sale &middot; Turnkey Build
                  </p>
                  <h3 className="font-display font-black text-2xl uppercase mb-3 leading-tight">
                    2027 Crew Cab Brush Truck
                  </h3>
                  <p className="text-white/85 text-sm leading-relaxed mb-6">
                    Choose your platform — Ford F-550 or Dodge Ram 5500. Fully equipped, ready to respond, starting under $300,000.
                  </p>
                  <Link
                    href="/crew-cab-brush-truck"
                    className="btn-gold mt-auto inline-flex w-fit items-center justify-center gap-2 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-[var(--tfrs-navy)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
                  >
                    <span>See the Build &amp; Pricing</span>
                    <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </RevealSection>
            <RevealSection delay={80} className="min-w-0">
              <div className="panel-elevated h-full flex flex-col border border-white/10 overflow-hidden" style={{ background: "var(--tfrs-navy)" }}>
                <img
                  src="/manus-storage/fleet-protection-illustration.png"
                  alt="Illustration of a municipal courthouse building at dusk with a lineup of police, fire, ambulance, and public works vehicles, representing Municipal Fleet Vehicle Protection"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto block"
                />
                <div className="p-8 flex flex-col flex-1">
                  <p className="font-eyebrow text-xs tracking-[0.3em] text-[#c9a84c] uppercase mb-3">
                    Fleet Protection Program
                  </p>
                  <h3 className="font-display font-black text-2xl uppercase mb-3 leading-tight">
                    Municipal Fleet Vehicle Protection
                  </h3>
                  <p className="text-white/85 text-sm leading-relaxed mb-6">
                    Turn unpredictable fleet repair costs into planned investments — $0-deductible coverage with
                    unlimited mileage/engine hours for law enforcement, fire, EMS, and public works fleets.
                  </p>
                  <Link
                    href="/municipal-fleet-vehicle-protection"
                    className="btn-gold mt-auto inline-flex w-fit items-center justify-center gap-2 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-[var(--tfrs-navy)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
                  >
                    <span>See Coverage Options</span>
                    <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* ── Photo Gallery ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <p className="font-eyebrow text-xs tracking-[0.3em] text-white/85 uppercase mb-3">
              Our Work
            </p>
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-display font-black text-4xl md:text-5xl uppercase leading-tight">
                Recent Builds
              </h2>
              <Link
                href="/gallery"
                className="font-eyebrow text-xs font-bold tracking-widest uppercase px-5 py-2.5 border border-white/20 text-white/80 hover:border-white/40 hover:text-white transition-colors inline-flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]"
              >
                View All <ArrowRight size={13} />
              </Link>
            </div>
          </RevealSection>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
            {GALLERY_IMAGES.map((img, i) => (
              <RevealSection key={img.src} delay={i * 60}>
                <div className="group relative overflow-hidden shadow-[0_2px_4px_rgba(0,0,0,0.2)] transition-shadow duration-300 hover:shadow-[0_12px_24px_rgba(0,0,0,0.5)]" style={{ aspectRatio: "4/3", background: "var(--tfrs-navy-panel)" }}>
                  <GalleryThumbnail image={img} caption={img.caption} fit={"fit" in img ? img.fit : undefined} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="font-eyebrow text-xs tracking-wider text-white/90 uppercase">
                      {img.caption}
                    </p>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Craftsmanship / Technical ── */}
      <section className="py-20 px-4 bg-[#0a1018]">
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <p className="font-eyebrow text-xs tracking-[0.3em] text-white/85 uppercase mb-3">
              Technical Expertise
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl uppercase mb-12 leading-tight">
              Built Right, Every Time
            </h2>
          </RevealSection>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Numbered capabilities */}
            <div className="grid gap-4">
              {[
                { num: "01", title: "Level IIIA Ballistic Panel Integration", desc: "Angel Armor NIJ Level IIIA door panels — all coverage levels available." },
                { num: "02", title: "OBD Digital Integration", desc: "OBD harnessing linking vehicle computers to control heads for lighting, brake logic, and park-kill." },
                { num: "03", title: "K9 Climate Control Safeguards", desc: "American Aluminum kennels + Ace K9 Heat Alarm Pro, Watchdog Monitor, 10\" fan kits, Hot N Pop remote door release." },
                { num: "04", title: "Skid Unit Fire Apparatus Assembly", desc: "500-gallon poly water tanks, Rowe RPM2/K19 pumps, 19HP Kohler EFI engines, Hannay hose reels, Akron bumper turrets." },
                { num: "05", title: "Shoreline Power Management", desc: "Kussmaul Auto Ejects, high-amp auto-chargers, Blue Sea power distribution blocks." },
                { num: "06", title: "Custom Weapon & Electronics Storage", desc: "Estes AWS cargo lockers, automated DECKED drawer systems, secure trunk risers." },
              ].map((item) => (
                <RevealSection key={item.num}>
                  <div className="panel-elevated flex gap-5 border border-white/8 bg-[var(--tfrs-navy-panel)] p-5">
                    <span className="font-display font-black text-3xl text-white/10 leading-none flex-shrink-0 w-10">
                      {item.num}
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-sm uppercase tracking-wide mb-1">
                        {item.title}
                      </h3>
                      <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </RevealSection>
              ))}
            </div>
            {/* Craft photo grid */}
            <div className="grid grid-cols-2 gap-1">
              {CRAFT_IMAGES.map((img, i) => (
                <RevealSection key={img.src} delay={i * 80}>
                  <div
                    className="group relative overflow-hidden shadow-[0_2px_4px_rgba(0,0,0,0.2)] transition-shadow duration-300 hover:shadow-[0_12px_24px_rgba(0,0,0,0.5)]"
                    style={{ aspectRatio: "4/3", background: "var(--tfrs-navy-panel)" }}
                  >
                    <img
                      src={img.src}
                      srcSet={img.srcSet}
                      sizes={img.sizes}
                      alt={img.caption}
                      width={img.width}
                      height={img.height}
                      loading="lazy"
                      decoding="async"
                      className={`w-full h-full transition-transform duration-500 ease-out group-hover:scale-110 ${
                        "fit" in img && img.fit === "contain" ? "object-contain" : "object-cover"
                      }`}
                    />
                    <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="font-eyebrow text-xs tracking-wider text-white/80 uppercase">
                        {img.caption}
                      </p>
                    </div>
                  </div>
                </RevealSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Why TFRS ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <p className="font-eyebrow text-xs tracking-[0.3em] text-white/85 uppercase mb-3">
              Why TFRS
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl uppercase mb-12 leading-tight">
              Our Differentiators
            </h2>
          </RevealSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DIFFERENTIATORS.map((d, i) => (
              <RevealSection key={d.title} delay={i * 70}>
                <div className="panel-elevated bg-[var(--tfrs-navy-panel)] border border-white/8 p-6 h-full">
                  <div className="text-[#c9a84c] mb-4">{d.icon}</div>
                  <h3 className="font-display font-bold text-sm uppercase tracking-wide mb-2 leading-tight">
                    {d.title}
                  </h3>
                  <p className="text-white/80 text-xs leading-relaxed">{d.description}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Trusted Agencies ── */}
      <section className="py-16 px-4 border-y border-white/10 bg-[#0a1018]">
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <p className="font-eyebrow text-xs tracking-[0.3em] text-white/85 uppercase text-center mb-8">
              Trusted by Federal &amp; Municipal Agencies
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {AGENCIES.map((a) => (
                <span
                  key={a}
                  className="font-eyebrow text-xs tracking-wider uppercase px-3 py-1.5 border border-white/15 text-white/85 hover:border-white/30 hover:text-white/85 transition-colors"
                >
                  {a}
                </span>
              ))}
            </div>
            <p className="text-center text-white/85 text-xs mt-6 font-eyebrow tracking-wider">
              50+ agencies served across Texas — federal, county/sheriff, municipal, and fire/EMS fleets
            </p>
          </RevealSection>
        </div>
      </section>

      {/* ── Cooperative Contracts ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <p className="font-eyebrow text-xs tracking-[0.3em] text-white/85 uppercase mb-3">
              Cooperative Purchasing
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl uppercase mb-4 leading-tight">
              No-Bid Contract Access
            </h2>
            <p className="text-white/85 mb-10 max-w-2xl">
              Eligible agencies can procure through cooperative contracts, bypassing lengthy competitive
              bidding while ensuring fair, pre-negotiated pricing.
            </p>
          </RevealSection>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                name: TIPS_SHORT_NAME,
                subtitle: `${TIPS_ORGANIZATION} · Contract #${TIPS_CONTRACT_NUMBER}`,
                description: "Cooperative purchasing contract for Texas and nationwide eligible agencies. Accessible to municipal, county, state, and federal agencies — eliminates competitive bid requirements for emergency vehicle procurement.",
              },
            ].map((c) => (
              <RevealSection key={c.name}>
                <div className="panel-elevated border border-white/10 bg-[var(--tfrs-navy-panel)] p-8">
                  <div className="font-display font-black text-4xl text-[#c9a84c] mb-1">{c.name}</div>
                  <div className="font-eyebrow text-xs tracking-widest text-white/85 uppercase mb-4">
                    {c.subtitle}
                  </div>
                  <p className="text-white/85 text-sm leading-relaxed">{c.description}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-4 bg-[#0a1018] text-center">
        <div className="max-w-3xl mx-auto">
          <RevealSection>
            <p className="font-eyebrow text-xs tracking-[0.3em] text-white/85 uppercase mb-4">
              Ready to Build
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase mb-6 leading-none">
              START YOUR<br />
              <span className="text-[#c9a84c]">CONFIGURATION</span>
            </h2>
            <p className="text-white/85 text-lg mb-10 leading-relaxed">
              Contact our team directly for cooperative contract pricing and a custom quote
              tailored to your fleet.
            </p>
            <div className="flex justify-center">
              <Link
                href="/quote"
                className="btn-cta text-sm px-8 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1018]"
              >
                <span>Request a Quote</span>
              </Link>
            </div>
            <p className="text-white/85 text-xs mt-8 font-eyebrow tracking-wider">
              CAGE Code: 9WTU3 · UEI: W55WVF2EC3J3 · SAM.gov: Active · NAICS: 811121
            </p>
          </RevealSection>
        </div>
      </section>

      {/* SBA Cert Lightbox */}
      {lightboxIndex !== null && (
        <Suspense fallback={null}>
          <SbaLightbox
            badges={SBA_BADGES}
            index={lightboxIndex}
            onClose={closeLightbox}
            onPrev={prevBadge}
            onNext={nextBadge}
          />
        </Suspense>
      )}

    </div>
  );
}